import InputField from '../components/ui/InputField';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BsCalendar2 } from 'react-icons/bs';
import { FaDownload, FaRegFolder } from 'react-icons/fa';
import { FiEdit3, FiSearch } from 'react-icons/fi';
import { LuUserPlus } from 'react-icons/lu';
import { IoReloadOutline } from 'react-icons/io5';
import {
  MdOutlineWorkOutline,
  MdOutlinePassword,
  MdOutlineShoppingBag,
  MdClose,
  MdLockOutline,
} from 'react-icons/md';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import PopupWrapper from '../components/utils/PopupWrapper';
import { usePopup } from '../hooks/usePopup';
import { useNavigate } from 'react-router-dom';
import { FaUsersGear } from 'react-icons/fa6';
import Button from '../components/ui/Button/Button';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchEmployees,
  fetchEmployeeById,
  searchEmployees,
} from '../redux/actions/employeeActions';
import { clearSearchResults } from '../redux/slices/employeeSlice';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import toast from 'react-hot-toast';
import { EmployeeData } from '../redux/types/employeeTypes';
import ActionButton from '../components/ui/ActionButton';
import Loading from '../components/utils/Loading';
import { RxDownload } from 'react-icons/rx';

// Define color scheme with hover colors and shadows
const colorSchemes = [
  {
    bgColor: 'bg-blue-50',
    hoverColor: 'bg-blue-100',
    shadowColor: 'drop-shadow-[0_10px_8px_rgba(59,130,246,0.15)]',
    hoverShadow: 'drop-shadow-[0_4px_8px_rgba(59,130,246,0.3)]',
  },
  {
    bgColor: 'bg-green-50',
    hoverColor: 'bg-green-100',
    shadowColor: 'drop-shadow-[0_10px_8px_rgba(34,197,94,0.15)]',
    hoverShadow: 'drop-shadow-[0_4px_8px_rgba(34,197,94,0.3)]',
  },
  {
    bgColor: 'bg-red-50',
    hoverColor: 'bg-red-100',
    shadowColor: 'drop-shadow-[0_10px_8px_rgba(239,68,68,0.15)]',
    hoverShadow: 'drop-shadow-[0_4px_8px_rgba(239,68,68,0.3)]',
  },
  {
    bgColor: 'bg-orange-50',
    hoverColor: 'bg-orange-100',
    shadowColor: 'drop-shadow-[0_10px_8px_rgba(249,115,22,0.15)]',
    hoverShadow: 'drop-shadow-[0_4px_8px_rgba(249,115,22,0.3)]',
  },
  {
    bgColor: 'bg-purple-50',
    hoverColor: 'bg-purple-100',
    shadowColor: 'drop-shadow-[0_10px_8px_rgba(168,85,247,0.15)]',
    hoverShadow: 'drop-shadow-[0_4px_8px_rgba(168,85,247,0.3)]',
  },
  {
    bgColor: 'bg-teal-50',
    hoverColor: 'bg-teal-100',
    shadowColor: 'drop-shadow-[0_10px_8px_rgba(20,184,166,0.15)]',
    hoverShadow: 'drop-shadow-[0_4px_8px_rgba(20,184,166,0.3)]',
  },
];

const Employees: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get redux state
  const { employees, currentEmployee, loading, error, pagination, searchResults, searching } =
    useAppSelector(state => state.employees);

  // Add initialLoading state
  const [initialLoading, setInitialLoading] = useState(true);
  // Add refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get user data and permissions from auth state
  const { user } = useAppSelector(state => state.auth);

  // Extract employee permissions from user data
  const [userPermissions, setUserPermissions] = useState({
    employeeRead: false,
    employeeWrite: false,
    employeeEdit: false,
    canManageEmployee: false,
  });

  // Track if permissions are loading
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // State for search
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State for visible employees
  const [visibleEmployees, setVisibleEmployees] = useState<EmployeeData[]>([]);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [page, setPage] = useState(1);

  // Add popup state
  const viewEmployeePopup = usePopup();
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(null);

  // Initialize our permission state from user data
  useEffect(() => {
    if (user) {
      setPermissionsLoading(true);
      // Super Admin has all permissions
      if (user.designation === 'superAdmin') {
        setUserPermissions({
          employeeRead: true,
          employeeWrite: true,
          employeeEdit: true,
          canManageEmployee: true,
        });
      } else if (user.permissions) {
        // Handle different permissions object formats
        let employeeReadValue = false;
        let employeeWriteValue = false;
        let employeeEditValue = false;
        let canManageEmployeeValue = false;

        // Check if permissions is an object with numeric keys (like {0: {...}})
        if (typeof user.permissions === 'object' && '0' in user.permissions) {
          //@ts-ignore
          employeeReadValue = Boolean(user.permissions[0].employeeRead);
          //@ts-ignore
          employeeWriteValue = Boolean(user.permissions[0].employeeWrite);
          //@ts-ignore
          employeeEditValue = Boolean(user.permissions[0].employeeEdit);
          //@ts-ignore
          canManageEmployeeValue = Boolean(user.permissions[0].canManageEmployee);
        }
        // Check if permissions is an array
        else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          employeeReadValue = Boolean(user.permissions[0].employeeRead);
          employeeWriteValue = Boolean(user.permissions[0].employeeWrite);
          employeeEditValue = Boolean(user.permissions[0].employeeEdit);
          canManageEmployeeValue = Boolean(user.permissions[0].canManageEmployee);
        }
        // Direct object access if neither of the above
        else if (typeof user.permissions === 'object') {
          employeeReadValue = Boolean(user.permissions.employeeRead);
          employeeWriteValue = Boolean(user.permissions.employeeWrite);
          employeeEditValue = Boolean(user.permissions.employeeEdit);
          canManageEmployeeValue = Boolean(user.permissions.canManageEmployee);
        }

        // Set the state with the determined values
        setUserPermissions({
          employeeRead: employeeReadValue,
          employeeWrite: employeeWriteValue,
          employeeEdit: employeeEditValue,
          canManageEmployee: canManageEmployeeValue,
        });
      }
      setPermissionsLoading(false);
    }
  }, [user]);

  // Use a ref to track if we've already shown the permission error
  const permissionErrorShown = useRef(false);

  // Fetch employees after permissions are initialized
  useEffect(() => {
    // Only run this effect if user data is available and permissions are loaded
    if (!user || permissionsLoading) return;

    const hasAccess = userPermissions.employeeRead || user?.designation === 'superAdmin';

    if (hasAccess) {
      setInitialLoading(true); // Set loading to true before fetch
      dispatch(fetchEmployees())
        .unwrap()
        .then(response => {
          if (response?.data) {
            setVisibleEmployees(response.data);
          }
          setInitialLoading(false); // Set loading to false after fetch completes
        })
        .catch(error => {
          console.error('Error fetching employees:', error);
          setInitialLoading(false); // Set loading to false on error
        });
    } else if (!permissionErrorShown.current) {
      // Only show the permission error once
      permissionErrorShown.current = true;
      toast.error("You don't have permission to view employees", {
        duration: 3000,
        id: 'permission-error', // Prevent duplicate toasts
      });
      setInitialLoading(false); // Make sure loading is turned off
    }
  }, [dispatch, userPermissions.employeeRead, user, permissionsLoading]);

  // Separate useEffect for authentication check
  useEffect(() => {
    // Redirect if not authenticated
    if (user === null) {
      toast.error('Please log in to access this page', {
        duration: 3000,
        id: 'login-required', // Prevent duplicate toasts
      });
      navigate('/auth/login');
    }
  }, [user, navigate]);

  // Effect for infinite scroll
  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100 &&
      !isFetchingMore &&
      pagination &&
      pagination.currentPage < pagination.pages
    ) {
      setIsFetchingMore(true);
      setPage(prev => prev + 1);
    }
  }, [isFetchingMore, pagination]);

  // Add scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Fetch more employees when page changes
  useEffect(() => {
    if (page > 1 && (userPermissions.employeeRead || user?.designation === 'superAdmin')) {
      setIsFetchingMore(true);
      dispatch(fetchEmployees({ page, limit: 10 }))
        .unwrap()
        .then(response => {
          if (response?.data) {
            // Make sure we're not adding duplicate data
            const newEmployees = response.data;
            const existingIds = new Set(visibleEmployees.map(emp => emp.employeeId));

            // Only add items that don't exist in the current list
            const uniqueNewEmployees = newEmployees.filter(
              (newEmp: EmployeeData) => !existingIds.has(newEmp.employeeId)
            );

            if (uniqueNewEmployees.length > 0) {
              setVisibleEmployees(prev => [...prev, ...uniqueNewEmployees]);
            }
          }
        })
        .catch(error => {
          console.error('Error fetching more employees:', error);
          // Reset page on error to allow retry
          setPage(prev => prev - 1);
        })
        .finally(() => {
          setIsFetchingMore(false);
        });
    }
  }, [page, dispatch, userPermissions.employeeRead, user, visibleEmployees]);

  // Handle employee click to view details
  const handleEmployeeClick = async (employee: EmployeeData) => {
    // Check if user has permission to view employee details
    if (!userPermissions.employeeRead && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to view employee details", {
        duration: 3000,
      });
      return;
    }

    setSelectedEmployee(employee);
    dispatch(fetchEmployeeById(employee.employeeId))
      .unwrap()
      .then(() => {
        viewEmployeePopup.openPopup();
      })
      .catch(error => {
        toast.error(`Error fetching employee details: ${error}`, {
          duration: 3000,
        });
      });
  };

  // Handle navigation to add employee page
  const handleAddEmployee = () => {
    // Check if user has permission to add employees
    if (!userPermissions.employeeWrite && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to add employees", {
        duration: 3000,
      });
      return;
    }
    navigate('/add-employee');
  };

  // Handle edit employee
  const handleEditEmployee = (employee: EmployeeData) => {
    // Check if user has permission to edit employees
    if (!userPermissions.employeeEdit && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to edit employees", {
        duration: 3000,
      });
      return;
    }
    navigate(`/edit-employee/${employee.employeeId}`);
  };

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.trim().length === 0) {
      dispatch(clearSearchResults());
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    // Set new timeout for debouncing
    searchTimeoutRef.current = setTimeout(() => {
      // Dispatch search action to get results from server
      dispatch(searchEmployees(query.trim()))
        .unwrap()
        .finally(() => {
          setIsSearching(false);
        });
    }, 300);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    dispatch(clearSearchResults());
    setIsSearching(false);
  };

  // Highlight matching text in search results
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim() || !text) return text;

    const regex = new RegExp(`(${query.trim()})`, 'gi');
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part: string, i: number) =>
          regex.test(part) ? (
            <span key={i} className="bg-yellow-200 font-medium">
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  // Add refresh function
  const handleRefresh = () => {
    const hasAccess = userPermissions.employeeRead || user?.designation === 'superAdmin';
    
    if (hasAccess) {
      setIsRefreshing(true);
      toast.loading('Refreshing employees data...', { id: 'refresh-employees' });
      
      // Clear search if active
      if (searchQuery) {
        setSearchQuery('');
        setIsSearching(false);
        dispatch(clearSearchResults());
      }
      
      dispatch(fetchEmployees())
        .unwrap()
        .then(response => {
          if (response?.data) {
            setVisibleEmployees(response.data);
            toast.success('Employees data refreshed successfully', { id: 'refresh-employees' });
          }
        })
        .catch(error => {
          console.error('Error refreshing employees:', error);
          toast.error(`Error refreshing employees data: ${error}`, { id: 'refresh-employees' });
        })
        .finally(() => {
          setIsRefreshing(false);
        });
    } else {
      toast.error("You don't have permission to refresh employees data", {
        duration: 3000,
      });
    }
  };

  // Check if user has permission to view employees
  const hasReadAccess = userPermissions.employeeRead || user?.designation === 'superAdmin';
  const hasWriteAccess = userPermissions.employeeWrite || user?.designation === 'superAdmin';
  const hasEditAccess = userPermissions.employeeEdit || user?.designation === 'superAdmin';

  // If permissions are still loading, show loading state
  if (permissionsLoading) {
    return (
      <ComponentWrapper icon={<FaUsersGear size={30} />} title="Employee Management">
        <div className="flex justify-center items-center h-64">
          <Loading height="200px" />
        </div>
      </ComponentWrapper>
    );
  }

  // If user has no permission at all, show access denied message
  if (!hasReadAccess && user !== null) {
    return (
      <ComponentWrapper
        icon={<MdLockOutline size={24} />}
        title="Access Denied"
        className="min-h-[70vh] flex items-center justify-center"
      >
        <div className="text-center py-12 max-w-lg mx-auto">
          <div className="bg-red-100 text-red-600 p-4 rounded-full inline-flex mb-6">
            <MdLockOutline size={48} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Permission Required</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access the Employee Management page. Please contact your
            administrator if you need access to this feature.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </ComponentWrapper>
    );
  }

  return (
    <ComponentWrapper
      icon={<FaUsersGear size={30} />}
      title="Employee Management"
      buttonsTwo={[
        {
          component: (
            <Button
              variant="secondary"
              size="sm"
              className="py-2 mr-2"
              icon={<IoReloadOutline />}
              onClick={handleRefresh}
              disabled={isRefreshing || initialLoading || loading}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          )
        },
        {
          component: hasWriteAccess ? (
            <Button
              variant="primary"
              size="sm"
              className="py-2"
              icon={<LuUserPlus />}
              onClick={handleAddEmployee}
            >
              Add Employee
            </Button>
          ) : null,
        },
      ]}
    >
      <div className="max-full">
        {/* Search Bar */}
        <div className="mt-6 mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {isSearching ? (
                <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-gray-400" />
              ) : (
                <FiSearch className="h-5 w-5 text-gray-400" />
              )}
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-10 mb-10 py-3 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
              placeholder="Search employees..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={handleClearSearch}
              >
                <MdClose className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Employees List */}
        {initialLoading ? (
          <div className="flex justify-center items-center ">
            <Loading height="400px" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Add Employee Card - Only shown if user has permission and not searching */}
            {(userPermissions.employeeWrite || user?.designation === 'superAdmin') &&
              !searchQuery && (
                <div
                  onClick={handleAddEmployee}
                  className="bg-lime-100 border-2 font-bold text-lime-700 border-lime-500 drop-shadow-[0_10px_8px_rgba(107,114,128,0.15)] rounded-2xl shadow p-6 flex items-center gap-4 justify-center 
                cursor-pointer transition-all duration-300 hover:bg-gray-100 hover:-translate-y-[4px] hover:shadow-lg hover:drop-shadow-[0_4px_8px_rgba(107,114,128,0.3)]
                group"
                >
                  <div className="mb-1 transition-transform duration-300 group-hover:scale-110">
                    <LuUserPlus size={44} className="h-full" />
                  </div>
                  <div className="flex flex-col items-left">
                    <div className="leading-tight text-lg group-hover:font-semibold">ADD</div>
                    <div className="leading-tight text-lg group-hover:font-semibold">EMPLOYEE</div>
                  </div>
                </div>
              )}

            {/* Show employees based on search state */}
            {searchQuery ? (
              // SEARCH MODE: Show search results or "no results" message
              searching ? (
                // Show loading indicator while searching
                <div className="col-span-3 text-center py-8">
                  <div className="flex justify-center">
                    <AiOutlineLoading3Quarters className="animate-spin h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 mt-2">Searching for employees...</p>
                </div>
              ) : searchResults.length > 0 ? (
                // Display search results
                searchResults.map((employee, index) => {
                  const colorIndex = index % colorSchemes.length;
                  const { bgColor, hoverColor, shadowColor, hoverShadow } =
                    colorSchemes[colorIndex];

                  return (
                    <div
                      key={employee.employeeId}
                      onClick={() => handleEmployeeClick(employee)}
                      className={`${bgColor} ${shadowColor} rounded-2xl shadow p-6
                        cursor-pointer transition-all duration-300 hover:${hoverColor} hover:-translate-y-[4px] hover:shadow-lg
                        hover:${hoverShadow}`}
                    >
                      <div className="flex items-start">
                        <div className="flex flex-col items-center mr-4">
                          <img
                            src={employee.profilePicture || 'https://placehold.co/50'}
                            alt={employee.fullname}
                            className="w-16 h-16 rounded-full object-cover border-2 border-white transition-transform duration-300 hover:scale-105"
                            onError={(e) => {
                              // If the image fails to load, fallback to placeholder
                              e.currentTarget.src = 'https://placehold.co/50';
                            }}
                          />
                          <div className="-mt-6 bg-white py-1 px-4 rounded-full text-xs text-gray-500 border border-gray-200">
                            {employee.employeeId}
                          </div>
                        </div>
                        <div className="flex-grow overflow-hidden">
                          <h3 className="text-xl font-semibold text-gray-800 truncate">
                            {highlightMatch(employee.fullname, searchQuery)}
                          </h3>
                          <p className="text-gray-500 text-sm truncate">
                            {highlightMatch(employee.email, searchQuery)}
                          </p>
                          <div className="mt-1 flex items-center gap-2 text-green-700 font-semibold text-sm truncate">
                            <FaRegFolder className="flex-shrink-0" />
                            <span className="truncate">
                              {employee.designation?.name || 'Employee'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                // No search results found
                <div className="col-span-3 text-center py-12">
                  <p className="text-gray-500 text-lg">
                    No employees found matching "{searchQuery}".
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    Try using different keywords or partial terms.
                  </p>
                </div>
              )
            ) : // NORMAL MODE: Show regular employee list when not searching
            visibleEmployees.length > 0 ? (
              // Regular employee list
              visibleEmployees.map((employee, index) => {
                const colorIndex = index % colorSchemes.length;
                const { bgColor, hoverColor, shadowColor, hoverShadow } = colorSchemes[colorIndex];

                return (
                  <div
                    key={employee.employeeId}
                    onClick={() => handleEmployeeClick(employee)}
                    className={`${bgColor} ${shadowColor} rounded-2xl shadow p-6
                        cursor-pointer transition-all duration-300 hover:${hoverColor} hover:-translate-y-[4px] hover:shadow-lg
                        hover:${hoverShadow}`}
                  >
                    <div className="flex items-start">
                      <div className="flex flex-col items-center mr-4">
                        <img
                          src={employee.profilePicture || 'https://placehold.co/50'}
                          alt={employee.fullname}
                          className="w-16 h-16 rounded-full object-cover border-2 border-white transition-transform duration-300 hover:scale-105"
                          onError={(e) => {
                            // If the image fails to load, fallback to placeholder
                            e.currentTarget.src = 'https://placehold.co/50';
                          }}
                        />
                        <div className="-mt-6 bg-white py-1 px-4 rounded-full text-xs text-gray-500 border border-gray-200">
                          {employee.employeeId}
                        </div>
                      </div>
                      <div className="flex-grow overflow-hidden">
                        <h3 className="text-xl font-semibold text-gray-800 truncate">
                          {employee.fullname}
                        </h3>
                        <p className="text-gray-500 text-sm truncate">{employee.email}</p>
                        <div className="mt-1 flex items-center gap-2 text-green-700 font-semibold text-sm truncate">
                          <FaRegFolder className="flex-shrink-0" />
                          <span className="truncate">
                            {employee.designation?.name || 'Employee'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              // No employees in the system
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-500 text-lg">
                  No employees found.{' '}
                  {userPermissions.employeeWrite || user?.designation === 'superAdmin'
                    ? 'Create your first employee to get started.'
                    : 'Please contact your administrator to add employees.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Loading indicator for infinite scroll */}
        {isFetchingMore && (
          <div className="flex justify-center items-center py-6 mt-4">
            <div className="relative">
              <div className="w-12 h-12">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-6 h-6 border-4 border-red-500 border-b-transparent rounded-full animate-spin"
                    style={{ animationDuration: '1.5s' }}
                  ></div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <span className="bg-gradient-to-r from-blue-600 to-red-500 bg-clip-text text-transparent font-medium">
                  Loading more employees...
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Employee Popup */}
      {selectedEmployee && currentEmployee && (
        <PopupWrapper
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3">
                <span className="text-4xl font-semibold">{currentEmployee.fullname}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-lg text-gray-700">{currentEmployee.designation?.name}</span>
                <span className="text-gray-400 font-bold text-sm">.</span>
                <span className="text-gray-500 font-medium text-sm">{currentEmployee.email}</span>
              </div>
            </div>
          }
          isOpen={viewEmployeePopup.isOpen}
          onClose={viewEmployeePopup.closePopup}
          size="lg"
          logo={
            <div className="relative flex-shrink-0">
              <img
                src={currentEmployee.profilePicture || 'https://placehold.co/50'}
                alt={currentEmployee.fullname}
                className="w-20 h-20 rounded-full object-cover border-2 border-indigo-100"
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/50';
                }}
              />
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gray-600 text-white text-xs font-bold rounded-full px-3 py-1 leading-none whitespace-nowrap">
                {currentEmployee.employeeId}
              </div>
            </div>
          }
        >
          <div className="flex flex-col py-6">
            {/* Address Information Section */}
            <div className="py-6 rounded-2xl">
              <h3 className="text-2xl font-bold text-gray-800 mb-10">Address Information</h3>
              <div className="grid grid-cols-2 gap-6">
                {currentEmployee.address && currentEmployee.address.length > 0 ? (
                  <>
                    <div className="col-span-2">
                      <InputField
                        label="Employee Address"
                        value={currentEmployee.address[0].address || 'Not specified'}
                        className="bg-gray-100"
                        disabled
                        icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                      />
                    </div>
                    <InputField
                      label="City"
                      value={currentEmployee.address[0].city || 'Not specified'}
                      className="bg-gray-100"
                      disabled
                      icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                    />
                    <InputField
                      label="State"
                      value={currentEmployee.address[0].state || 'Not specified'}
                      className="bg-gray-100"
                      disabled
                      icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                    />
                    <InputField
                      label="Role"
                      value={currentEmployee.address[0].role || 'Not specified'}
                      className="bg-gray-100"
                      disabled
                      icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                    />
                    <InputField
                      label="Postal Code"
                      value={currentEmployee.address[0].pinCode?.toString() || 'Not specified'}
                      className="bg-gray-100"
                      disabled
                      icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                    />
                  </>
                ) : (
                  <div className="col-span-2 text-center text-gray-500">
                    No address information available
                  </div>
                )}
              </div>
            </div>

            {/* Job Related Information */}
            <div className="py-6 rounded-2xl">
              <h3 className="text-2xl font-bold text-gray-800 mb-8">Job Related Information</h3>
              <div className="grid grid-cols-2 gap-6">
                <InputField
                  label="Department"
                  value={currentEmployee.designation?.name || 'Not specified'}
                  className="bg-gray-100"
                  disabled
                  icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                />
                <InputField
                  label="Date of Joining"
                  value={
                    currentEmployee.dateOfJoining
                      ? new Date(currentEmployee.dateOfJoining).toLocaleDateString()
                      : 'Not specified'
                  }
                  className="bg-gray-100"
                  disabled
                  icon={<BsCalendar2 className="w-5 h-5" />}
                />
                <InputField
                  label="Yearly CTC"
                  value={
                    currentEmployee.ctc
                      ? `₹${currentEmployee.ctc.toLocaleString()}`
                      : 'Not specified'
                  }
                  className="bg-gray-100"
                  disabled
                  icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                />
                <InputField
                  label="Education"
                  value={currentEmployee.education || 'Not specified'}
                  className="bg-gray-100"
                  disabled
                  icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                />
              </div>
            </div>

            {/* Employee Status Section */}
            <div className="py-6 rounded-2xl">
              <h3 className="text-2xl font-bold text-gray-800 mb-8">Employee Status</h3>
              <div className="grid grid-cols-2 gap-6">
                <InputField
                  label="Working Status"
                  value={currentEmployee.employeeStatus || 'Not specified'}
                  className="bg-gray-100"
                  disabled
                  icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                />
                <InputField
                  label="Job Location"
                  value={currentEmployee.jobLocation || 'Not specified'}
                  className="bg-gray-100"
                  disabled
                  icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                />
              </div>
            </div>

            {/* Record Information Section */}
            <div className="py-6 rounded-2xl">
              <h3 className="text-2xl font-bold text-gray-800 mb-8">Record Information</h3>
              <div className="grid grid-cols-2 gap-6">
                <InputField
                  label="Created on"
                  value={
                    currentEmployee.createdAt
                      ? new Date(currentEmployee.createdAt).toLocaleDateString()
                      : 'Not specified'
                  }
                  className="bg-gray-100"
                  disabled
                  icon={<BsCalendar2 className="w-5 h-5" />}
                />
                <InputField
                  label="Date of Birth"
                  value={
                    currentEmployee.dob
                      ? new Date(currentEmployee.dob).toLocaleDateString()
                      : 'Not specified'
                  }
                  className="bg-gray-100"
                  disabled
                  icon={<BsCalendar2 className="w-5 h-5" />}
                />
              </div>
            </div>

            {/* Permissions Section */}
            <div className="py-6 rounded-2xl">
              <h3 className="text-2xl font-bold text-gray-800 mb-8">Permissions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {currentEmployee.designation?.permissions && currentEmployee.designation?.permissions.length > 0 ? (
                  <>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-700 mb-2">Employee Management</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Read</span>
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            currentEmployee.designation.permissions[0]?.employeeRead 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {currentEmployee.designation.permissions[0]?.employeeRead ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Create</span>
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            currentEmployee.designation.permissions[0]?.employeeWrite 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {currentEmployee.designation.permissions[0]?.employeeWrite ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Edit</span>
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            currentEmployee.designation.permissions[0]?.employeeEdit 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {currentEmployee.designation.permissions[0]?.employeeEdit ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 font-medium">Manage Employee</span>
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            currentEmployee.designation.permissions[0]?.canManageEmployee 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {currentEmployee.designation.permissions[0]?.canManageEmployee ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-700 mb-2">Application Access</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Application Access</span>
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            currentEmployee.applicationAccess 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {currentEmployee.applicationAccess ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="col-span-2 text-center text-gray-500">
                    No permission information available
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons - Only enable if user has permission */}
            <div className="flex gap-6 mt-6">
              <button
                onClick={() => handleEditEmployee(currentEmployee)}
                className="flex-1 px-4 py-4 bg-lime-100 text-lg font-semibold rounded-md hover:bg-lime-200 transition-colors flex items-center border-2 border-lime-600 justify-center gap-2 text-lime-600"
                disabled={!userPermissions.employeeEdit && user?.designation !== 'superAdmin'}
              >
                <FiEdit3 />
                Edit
              </button>
            </div>

            {/* Permission Info */}
            {!userPermissions.employeeEdit && user?.designation !== 'superAdmin' && (
              <div className="text-center text-gray-500 mt-4">
                <p>You don't have permission to edit employee information.</p>
              </div>
            )}
          </div>
        </PopupWrapper>
      )}

      {/* Mobile Action Button - only show if user has permission */}
      {(userPermissions.employeeWrite || user?.designation === 'superAdmin') && (
        <ActionButton
          onClick={handleAddEmployee}
          icon={<LuUserPlus size={20} />}
          variant="primary"
          fullWidth={true}
          mobileFixed={true}
          className="md:hidden"
          disabled={loading}
        >
          Add Employee
        </ActionButton>
      )}
    </ComponentWrapper>
  );
};

export default Employees;
